/**
 * The 84 checklist items, grouped by section. Filter state is owned by the page and mirrored
 * into the URL, so a filtered view can be linked to.
 */
import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, MessageSquare } from 'lucide-react';
import Button from '../../../components/Button';
import EmptyState from '../../../components/EmptyState';
import { STATUS_LABELS, type HipaaItem, type HipaaStatus } from '../types/hipaaTypes';
import { breakdownOf } from '../utils/hipaaStats';
import {
  DEFAULT_FILTERS,
  matchesFilters,
  type ChecklistFilters,
} from '../utils/checklistFilters';
import {
  BlockedBadge,
  EffortBadge,
  PriorityBadge,
  SpecBadge,
  StatusPill,
  VerificationBadge,
} from './Badges';
import { StackedProgressBar } from './Charts';

const FilterGroup = <T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) => (
  <div className="flex items-center gap-1.5">
    <span className="text-xs text-gray-400">{label}</span>
    <div className="flex flex-wrap gap-1">
      {options.map((option) => (
        <Button
          key={option.value}
          size="sm"
          variant={value === option.value ? 'primary' : 'outline'}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </Button>
      ))}
    </div>
  </div>
);

export const ChecklistFilterBar = ({
  filters,
  onChange,
  resultCount,
  totalCount,
}: {
  filters: ChecklistFilters;
  onChange: (filters: ChecklistFilters) => void;
  resultCount: number;
  totalCount: number;
}) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-4 space-y-2.5">
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
      <FilterGroup
        label="Status"
        value={filters.status}
        onChange={(status) => onChange({ ...filters, status })}
        options={[
          { value: 'all', label: 'All' },
          { value: 'open', label: 'Open' },
          ...(Object.keys(STATUS_LABELS) as HipaaStatus[]).map((status) => ({
            value: status,
            label: STATUS_LABELS[status],
          })),
        ]}
      />
      <FilterGroup
        label="Priority"
        value={filters.priority}
        onChange={(priority) => onChange({ ...filters, priority })}
        options={[
          { value: 'all', label: 'All' },
          { value: 'P0', label: 'P0' },
          { value: 'P1', label: 'P1' },
          { value: 'P2', label: 'P2' },
        ]}
      />
      <FilterGroup
        label="Spec"
        value={filters.spec}
        onChange={(spec) => onChange({ ...filters, spec })}
        options={[
          { value: 'all', label: 'All' },
          { value: 'R', label: 'Required' },
          { value: 'A', label: 'Addressable' },
        ]}
      />
      <FilterGroup
        label="Flag"
        value={filters.flag}
        onChange={(flag) => onChange({ ...filters, flag })}
        options={[
          { value: 'all', label: 'All' },
          { value: 'blocked', label: 'Blocked' },
          { value: 'verify', label: 'Needs verification' },
        ]}
      />
    </div>

    <div className="flex items-center gap-3">
      <input
        type="text"
        value={filters.search}
        onChange={(event) => onChange({ ...filters, search: event.target.value })}
        placeholder="Search titles and notes…"
        className="flex-1 min-w-0 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
      />
      <span className="text-xs text-gray-400 whitespace-nowrap">
        {resultCount} of {totalCount}
      </span>
      <Button size="sm" variant="ghost" onClick={() => onChange(DEFAULT_FILTERS)}>
        Reset
      </Button>
    </div>
  </div>
);

const ItemRow = ({ item, onOpen }: { item: HipaaItem; onOpen: (item: HipaaItem) => void }) => (
  <button
    type="button"
    onClick={() => onOpen(item)}
    className="w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors flex flex-wrap sm:flex-nowrap items-start gap-3"
  >
    <span className="shrink-0 pt-0.5">
      <StatusPill status={item.status} />
    </span>
    <span className="flex-1 min-w-0">
      <span className="block text-sm text-gray-800">{item.title}</span>
      {item.statusNote && (
        <span className="block text-xs text-gray-400 truncate mt-0.5">{item.statusNote}</span>
      )}
    </span>
    <span className="shrink-0 flex flex-wrap items-center gap-1.5 w-full sm:w-auto">
      {item.commentCount > 0 && (
        <span className="inline-flex items-center gap-0.5 text-[11px] text-gray-400">
          <MessageSquare size={11} />
          {item.commentCount}
        </span>
      )}
      {item.needsVerification && <VerificationBadge />}
      {item.blocked && <BlockedBadge reason={item.blockedReason} />}
      <EffortBadge effort={item.effort} />
      <SpecBadge specs={item.specs} />
      <PriorityBadge priorities={item.priorities} />
    </span>
  </button>
);

interface ChecklistTabProps {
  items: HipaaItem[];
  allItems: HipaaItem[];
  filters: ChecklistFilters;
  onFiltersChange: (filters: ChecklistFilters) => void;
  onOpenItem: (item: HipaaItem) => void;
}

export const ChecklistTab = ({
  items,
  allItems,
  filters,
  onFiltersChange,
  onOpenItem,
}: ChecklistTabProps) => {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const visible = useMemo(
    () => items.filter((item) => matchesFilters(item, filters)),
    [items, filters],
  );

  const groups = useMemo(() => {
    const bySection = new Map<string, HipaaItem[]>();
    for (const item of visible) {
      const bucket = bySection.get(item.section);
      if (bucket) bucket.push(item);
      else bySection.set(item.section, [item]);
    }
    return [...bySection.entries()];
  }, [visible]);

  const toggle = (section: string) =>
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });

  return (
    <div>
      <ChecklistFilterBar
        filters={filters}
        onChange={onFiltersChange}
        resultCount={visible.length}
        totalCount={items.length}
      />

      {groups.length === 0 ? (
        <EmptyState
          title="Nothing matches"
          description="No checklist items match the current filters."
          action={{ label: 'Reset filters', onClick: () => onFiltersChange(DEFAULT_FILTERS) }}
        />
      ) : (
        <div className="space-y-3">
          {groups.map(([section, sectionItems]) => {
            const isCollapsed = collapsed.has(section);
            return (
              <div
                key={section}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggle(section)}
                  className="w-full flex flex-wrap sm:flex-nowrap items-center gap-2 px-4 py-2.5 text-left bg-gray-50 border-b border-gray-100 hover:bg-gray-100 transition-colors"
                >
                  {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                  <span className="text-sm font-semibold text-gray-800">
                    <span className="font-mono text-gray-400">§{section}</span>{' '}
                    {sectionItems[0].sectionTitle}
                  </span>
                  {sectionItems[0].sectionCfr && (
                    <span className="text-[11px] text-gray-400">{sectionItems[0].sectionCfr}</span>
                  )}
                  <span className="ml-auto flex items-center gap-2">
                    <span className="w-24">
                      <StackedProgressBar breakdown={breakdownOf(sectionItems, allItems)} />
                    </span>
                    <span className="text-xs text-gray-400">{sectionItems.length}</span>
                  </span>
                </button>
                {!isCollapsed && (
                  <div className="divide-y divide-gray-50">
                    {sectionItems.map((item) => (
                      <ItemRow key={item.id} item={item} onOpen={onOpenItem} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
